import React from 'react';
import { Button as AntButton } from 'antd';

/**
 * @typedef {'primary' | 'secondary' | 'ghost' | 'danger'} ButtonVariant
 * @typedef {'sm' | 'md' | 'lg'} ButtonSize
 */

const VARIANT_MAP = {
  primary:   { type: 'primary', danger: false },
  secondary: { type: 'default', danger: false },
  ghost:     { type: 'link',    danger: false },
  danger:    { type: 'primary', danger: true  },
  text:      { type: 'text',    danger: false },
};

const SIZE_MAP = {
  sm: 'small',
  md: 'middle',
  lg: 'large',
};

/**
 * Shared Button component — maps variant/size to antd Button props.
 * Never pass raw antd `type` prop; use `variant` instead.
 *
 * @param {object}        props
 * @param {ButtonVariant} [props.variant='primary']  - 'primary'|'secondary'|'ghost'|'danger'|'text'
 * @param {ButtonSize}    [props.size='md']
 * @param {boolean}       [props.loading]
 * @param {React.ReactNode} [props.icon]
 * @param {boolean}       [props.block]
 * @param {boolean}       [props.disabled]
 * @param {boolean}       [props.danger]    - augments variant (e.g. ghost+danger → red link)
 * @param {() => void}    [props.onClick]
 * @param {string}        [props.className]
 * @param {string}        [props.aria-label] - required when the button has no text
 * @param {React.ReactNode} props.children
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  loading,
  icon,
  block,
  disabled,
  danger,
  onClick,
  className,
  children,
  style,
  'aria-label': ariaLabel,
  // Destructure antd `type` so it never leaks through rest
  // eslint-disable-next-line no-unused-vars
  type: _type,
  ...rest
}) => {
  const map = VARIANT_MAP[variant] ?? VARIANT_MAP.primary;
  const isDanger = danger !== undefined ? danger : map.danger;
  const isIconOnly = Boolean(icon && !children);

  return (
    <AntButton
      type={map.type}
      danger={isDanger}
      size={SIZE_MAP[size]}
      loading={loading}
      icon={icon}
      block={block}
      disabled={disabled}
      onClick={onClick}
      className={`${className || ''} ${isIconOnly ? 'common-button--icon-only' : ''}`.trim()}
      aria-label={ariaLabel}
      style={{
        ...(isIconOnly
          ? { inlineSize: 40, minInlineSize: 40, paddingInline: 0 }
          : {}),
        ...style,
      }}
      {...rest}
    >
      {children}
    </AntButton>
  );
};

export default Button;
